/**
 * @file Data-driven enemy population tables for the dungeon generator (issue #44).
 *
 * Mirrors the vocabulary of {@link ./Waves.js} (`weights` / `unlocks`) so the
 * two spawners speak the same language. The generator reads these tables to
 * decide, deterministically from the dungeon seed, *how many* enemies a room
 * gets, *which archetypes*, and *what spatial formation* they hold.
 *
 * Everything here is plain data — the generator stays pure (no Three.js) and
 * adding an archetype or a formation is a new row, never a new code path.
 */

/**
 * Per-room-type enemy count ranges, before difficulty and per-floor scaling.
 * @type {Readonly<Record<string, { min: number, max: number }>>}
 */
export const ROOM_BUDGET = Object.freeze({
  combat: { min: 2, max: 6 },
  elite: { min: 3, max: 5 },
  boss: { min: 2, max: 3 },
  treasure: { min: 0, max: 1 },
  shrine: { min: 0, max: 1 }
});

/**
 * Pick weights per room type. A missing archetype simply cannot roll in that
 * room type. Grunts stay common so a room never becomes all-specials.
 * @type {Readonly<Record<string, Record<string, number>>>}
 */
export const ROOM_WEIGHTS = Object.freeze({
  combat: { grunt: 10, runner: 7, spitter: 5, exploder: 4, brute: 1 },
  elite: { brute: 6, spitter: 5, exploder: 4, runner: 3, grunt: 2 },
  boss: { brute: 8, spitter: 4, exploder: 3, grunt: 2 },
  treasure: { runner: 6, spitter: 4, grunt: 3 },
  shrine: { spitter: 6, runner: 4, grunt: 3 }
});

/**
 * Minimum room difficulty (0..1, from BFS depth) before an archetype may appear.
 * The depth-gating analogue of {@link ./Waves.js}'s `unlocks`: heavy archetypes
 * never show up in the shallow rooms right off the entrance.
 * @type {Readonly<Record<string, number>>}
 */
export const DIFFICULTY_UNLOCKS = Object.freeze({
  grunt: 0,
  runner: 0.08,
  spitter: 0.28,
  exploder: 0.5,
  brute: 0.62
});

/** Which archetypes fight in melee vs. at range (for formation preferences). */
export const ARCHETYPE_ROLE = Object.freeze({
  grunt: 'melee',
  runner: 'melee',
  brute: 'melee',
  exploder: 'melee',
  spitter: 'ranged'
});

/**
 * Variety guarantees so a populated room is not eight identical grunts.
 */
export const VARIETY = Object.freeze({
  /** A room with at least this many members must field at least 2 distinct types. */
  minDistinctTypes: 2,
  /** No single archetype may exceed this fraction of a room's roster. */
  maxSameTypeRatio: 0.7,
  /** Consecutive populated rooms halve the weight of the previous room's types. */
  recentPenalty: 0.5
});

/**
 * Formation catalog. The generator selects a row by weight (filtered by room
 * type / size / member count) — a data table, not a `switch` — then realises the
 * chosen arrangement geometrically.
 *
 * @typedef {object} Formation
 * @property {string} id Arrangement key the generator knows how to realise.
 * @property {number} weight Relative selection weight.
 * @property {string[]|'*'} roomTypes Room types this formation suits.
 * @property {number} minRoomSize `min(w,h)` required, in tiles.
 * @property {number} minMembers Smallest group this shape is used for.
 * @property {number} maxMembers Largest group this shape is used for.
 * @property {'any'|'melee'|'ranged'|'mixed'} prefer Archetype-role bias.
 *
 * @type {ReadonlyArray<Formation>}
 */
export const FORMATIONS = Object.freeze([
  { id: 'scatter', weight: 5, roomTypes: '*', minRoomSize: 0, minMembers: 1, maxMembers: 99, prefer: 'any' },
  { id: 'ring', weight: 2, roomTypes: ['combat', 'elite', 'boss'], minRoomSize: 9, minMembers: 4, maxMembers: 8, prefer: 'any' },
  { id: 'cluster', weight: 3, roomTypes: ['combat', 'elite'], minRoomSize: 6, minMembers: 3, maxMembers: 8, prefer: 'melee' },
  { id: 'line', weight: 2, roomTypes: ['combat', 'elite'], minRoomSize: 8, minMembers: 3, maxMembers: 6, prefer: 'mixed' },
  { id: 'guard', weight: 2, roomTypes: ['combat', 'elite', 'treasure', 'shrine'], minRoomSize: 6, minMembers: 2, maxMembers: 4, prefer: 'melee' },
  { id: 'ambush', weight: 2, roomTypes: ['combat', 'elite'], minRoomSize: 9, minMembers: 2, maxMembers: 5, prefer: 'any' }
]);

/**
 * Default population knobs. `CONFIG.dungeon.population` in
 * {@link ./GameConfig.js} overrides these at runtime; kept here so the tables
 * are self-describing and usable headless in tests.
 */
export const POPULATION_DEFAULTS = Object.freeze({
  enabled: true,
  /** 'prepopulated' | 'waves' | 'both'. */
  mode: 'prepopulated',
  minEnemies: 14,
  maxEnemies: 46,
  /** Hard ceiling, coherent with WAVES.maxAlive. */
  maxPrePlaced: 90,
  /** World units around the entrance kept clear of enemies. */
  safeRadiusFromEntrance: 9,
  /** Per-floor enemy multiplier growth and its cap. */
  scaleWithLevel: { perLevel: 0.12, max: 2.5 }
});
